'use client';

import React, { useState } from 'react';
import { Mic, Radio, Loader2 } from 'lucide-react';

export default function AiCommentator({ mode = 'full' }: { mode?: 'full' | 'header' }) {
  const [ttsText, setTtsText] = useState('');
  const [lastMessage, setLastMessage] = useState("System bereit. Warte auf Befehle...");
  const [isLoading, setIsLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const lastEventId = React.useRef(0);

  // Poll for latest message to display in header (optional logic if we still want to animate Mic)
  React.useEffect(() => {
    // We can keep listening to events to animate the mic even if we type here
    const interval = setInterval(async () => {
      try {
        const res = await fetch('/api/events');
        const event = await res.json();
        
        if (event.id && event.id > lastEventId.current) {
          if (event.type === 'tts') {
            setLastMessage(event.payload);
            setIsSpeaking(true);
            setTimeout(() => setIsSpeaking(false), 5000); 
          }
          lastEventId.current = event.id;
        }
      } catch (e) {}
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Moved Function UP so it's accessible in the header condition
  const broadcastTTS = async () => {
    if (!ttsText) return;
    setIsLoading(true);
    
    try {
      await fetch('/api/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'tts', payload: ttsText }),
      });
      setTtsText('');
    } catch (e) {
      console.error("Broadcast failed");
    } finally {
      setIsLoading(false);
    }
  };

  if (mode === 'header') {
    return (
      <div className="flex items-center gap-2 bg-slate-900/50 border border-slate-800 rounded-lg px-2 py-1.5 w-full backdrop-blur-sm">
        <div className="text-purple-500 pl-2">
           <Mic size={16} className={isSpeaking ? 'animate-pulse text-green-400' : ''} />
        </div>
        <input
          type="text"
          value={ttsText}
          onChange={(e) => setTtsText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && broadcastTTS()}
          disabled={isLoading}
          placeholder="KI Durchsage machen... (für alle hörbar)"
          className="flex-1 bg-transparent border-none text-sm text-white focus:ring-0 placeholder:text-slate-600 outline-none h-8"
        />
        <button
          onClick={broadcastTTS}
          disabled={isLoading}
          className="bg-purple-600 hover:bg-purple-500 text-white p-1.5 rounded-md transition-colors"
        >
          {isLoading ? <Loader2 size={14} className="animate-spin" /> : <Radio size={14} />}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-slate-900 border border-slate-700 rounded-xl p-4 shadow-2xl">
      <div className="flex items-center gap-2 mb-3 text-white text-sm uppercase font-bold tracking-wider">
        <Mic className="w-5 h-5 text-purple-500" /> KI-Kommentator
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={ttsText}
          onChange={(e) => setTtsText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && broadcastTTS()}
          disabled={isLoading}
          placeholder={isLoading ? "Sende an alle..." : "Nachricht schreiben... (für alle hörbar)"}
          className="flex-1 bg-slate-950 border border-slate-800 text-white rounded-lg px-4 py-3 focus:outline-none focus:border-purple-500 disabled:opacity-50 font-medium"
        />
        <button
          onClick={broadcastTTS}
          disabled={isLoading}
          className="bg-purple-600 hover:bg-purple-500 disabled:bg-slate-700 text-white px-6 rounded-lg font-bold transition-colors flex items-center justify-center min-w-[80px]"
        >
          {isLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Radio className="w-6 h-6" />}
        </button>
      </div>
    </div>
  );
}